import { Application } from 'express';
import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';
import { DatabaseConnection } from '@unidev-hub/database';
import { ServerHooks } from '../../types';

/**
 * Execute beforeInit hook if provided
 *
 * @param hooks ServerHooks object
 * @param logger Logger instance
 */
export async function executeBeforeInit(
  hooks: ServerHooks | undefined,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.beforeInit) {
      logger.debug('Executing beforeInit hook');
      await hooks.beforeInit(logger);
    }
  } catch (error) {
    logger.error('Error in beforeInit hook', error as Error);
    throw error;
  }
}

/**
 * Execute afterConfigLoaded hook if provided
 *
 * @param hooks ServerHooks object
 * @param config ConfigManager instance
 * @param logger Logger instance
 */
export async function executeAfterConfigLoaded(
  hooks: ServerHooks | undefined,
  config: ConfigManager,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.afterConfigLoaded) {
      logger.debug('Executing afterConfigLoaded hook');
      await hooks.afterConfigLoaded(config, logger);
    }
  } catch (error) {
    logger.error('Error in afterConfigLoaded hook', error as Error);
    throw error;
  }
}

/**
 * Execute afterApiCreated hook if provided
 *
 * @param hooks ServerHooks object
 * @param app Express application
 * @param config ConfigManager instance
 * @param logger Logger instance
 */
export async function executeAfterApiCreated(
  hooks: ServerHooks | undefined,
  app: Application,
  config: ConfigManager,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.afterApiCreated) {
      logger.debug('Executing afterApiCreated hook');
      await hooks.afterApiCreated(app, config, logger);
    }
  } catch (error) {
    logger.error('Error in afterApiCreated hook', error as Error);
    throw error;
  }
}

/**
 * Execute afterDatabaseInit hook if provided
 *
 * @param hooks ServerHooks object
 * @param dbConnection Database connection
 * @param config ConfigManager instance
 * @param logger Logger instance
 */
export async function executeAfterDatabaseInit(
  hooks: ServerHooks | undefined,
  dbConnection: DatabaseConnection,
  config: ConfigManager,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.afterDatabaseInit) {
      logger.debug('Executing afterDatabaseInit hook');
      await hooks.afterDatabaseInit(dbConnection, config, logger);
    }
  } catch (error) {
    logger.error('Error in afterDatabaseInit hook', error as Error);
    throw error;
  }
}

/**
 * Execute afterRoutesSetup hook if provided
 *
 * @param hooks ServerHooks object
 * @param app Express application
 * @param config ConfigManager instance
 * @param logger Logger instance
 */
export async function executeAfterRoutesSetup(
  hooks: ServerHooks | undefined,
  app: Application,
  config: ConfigManager,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.afterRoutesSetup) {
      logger.debug('Executing afterRoutesSetup hook');
      await hooks.afterRoutesSetup(app, config, logger);
    }
  } catch (error) {
    logger.error('Error in afterRoutesSetup hook', error as Error);
    throw error;
  }
}

/**
 * Execute beforeServerStart hook if provided
 *
 * @param hooks ServerHooks object
 * @param app Express application
 * @param config ConfigManager instance
 * @param logger Logger instance
 */
export async function executeBeforeServerStart(
  hooks: ServerHooks | undefined,
  app: Application,
  config: ConfigManager,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.beforeServerStart) {
      logger.debug('Executing beforeServerStart hook');
      await hooks.beforeServerStart(app, config, logger);
    }
  } catch (error) {
    logger.error('Error in beforeServerStart hook', error as Error);
    throw error;
  }
}

/**
 * Execute afterServerStart hook if provided
 *
 * @param hooks ServerHooks object
 * @param port Server port
 * @param host Server host
 * @param environment Environment name
 * @param app Express application
 * @param logger Logger instance
 */
export async function executeAfterServerStart(
  hooks: ServerHooks | undefined,
  port: number,
  host: string,
  environment: string,
  app: Application,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.afterServerStart) {
      logger.debug('Executing afterServerStart hook');
      await hooks.afterServerStart(port, host, environment, app, logger);
    }
  } catch (error) {
    logger.error('Error in afterServerStart hook', error as Error);
    // Don't throw as the server is already started
    logger.warn('Continuing despite error in afterServerStart hook');
  }
}

/**
 * Execute beforeShutdown hook if provided
 *
 * @param hooks ServerHooks object
 * @param logger Logger instance
 */
export async function executeBeforeShutdown(
  hooks: ServerHooks | undefined,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.beforeShutdown) {
      logger.debug('Executing beforeShutdown hook');
      await hooks.beforeShutdown(logger);
    }
  } catch (error) {
    logger.error('Error in beforeShutdown hook', error as Error);
    // Continue shutdown process despite errors
  }
}

/**
 * Execute afterDatabaseClosed hook if provided
 *
 * @param hooks ServerHooks object
 * @param logger Logger instance
 */
export async function executeAfterDatabaseClosed(
  hooks: ServerHooks | undefined,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.afterDatabaseClosed) {
      logger.debug('Executing afterDatabaseClosed hook');
      await hooks.afterDatabaseClosed(logger);
    }
  } catch (error) {
    logger.error('Error in afterDatabaseClosed hook', error as Error);
    // Continue shutdown process despite errors
  }
}

/**
 * Execute afterServerClosed hook if provided
 *
 * @param hooks ServerHooks object
 * @param logger Logger instance
 */
export async function executeAfterServerClosed(
  hooks: ServerHooks | undefined,
  logger: Logger
): Promise<void> {
  try {
    if (hooks?.afterServerClosed) {
      logger.debug('Executing afterServerClosed hook');
      await hooks.afterServerClosed(logger);
    }
  } catch (error) {
    logger.error('Error in afterServerClosed hook', error as Error);
    // Continue shutdown process despite errors
  }
}
